import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Check, LayoutGrid, MoreHorizontal, Pencil, Plus, RotateCcw, Star, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { AddWidgetsDialog } from '@/components/dashboard/AddWidgetsDialog'
import { isWidgetBuilt } from '@/components/dashboard/builtWidgets'
import { DashboardGrid } from '@/components/dashboard/DashboardGrid'
import { DashboardNameDialog } from '@/components/dashboard/DashboardNameDialog'
import { DashboardPeriodProvider, DashboardPeriodSelect } from '@/components/dashboard/DashboardPeriod'
import { SaveFailedNote } from '@/components/dashboard/WidgetCard'
import { useAllowedWidgets, useDashboards } from '@/hooks/useDashboardLayouts'
import {
  canAddDashboard,
  mergeHiddenWidgets,
  presetWidgets,
  sameWidgets,
  setWidgetView,
  starterOf,
  type WidgetPlacement,
} from '@/lib/dashboardLayouts'
import type { WidgetView } from '@/lib/dashboardWidgets'
import { cn } from '@/lib/utils'

/**
 * The Dashboards page (z8nrz7eqyu): the user's own named dashboards, opened
 * from the Home menu's Dashboards tile. Every user starts with three (Overview,
 * Manufacturing & Process, Patients & Quality), opens on their default (★),
 * and can edit, rename or delete any of them, or make another the default.
 * Personal, per tenant, stored apart from Home, so arranging one never moves a
 * widget on Home.
 */
export function DashboardsPage() {
  const boards = useDashboards()
  const { allowed, isAllowed } = useAllowedWidgets()
  // The dashboard being viewed rides in the URL (?d=), so a refresh, a link or
  // the back button reopens the same one rather than jumping to the default.
  const [params, setParams] = useSearchParams()
  const viewingId = params.get('d')
  const setViewingId = (id: string | null) =>
    setParams(
      prev => {
        const next = new URLSearchParams(prev)
        if (id) next.set('d', id)
        else next.delete('d')
        return next
      },
      { replace: true },
    )
  const [draft, setDraft] = useState<WidgetPlacement[] | null>(null)
  const [picking, setPicking] = useState(false)
  const [naming, setNaming] = useState<'new' | 'rename' | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Opens on the default; a tab click views another without changing it.
  const active = boards.prefs.dashboards.find(d => d.id === viewingId) ?? boards.defaultDashboard
  const isDefault = active?.id === boards.prefs.defaultId
  const starter = active ? starterOf(active.id) : undefined
  const changedFromStarter = !!starter && !!active && !sameWidgets(active.widgets, starter.widgets)
  // A widget not built yet, or one the role cannot see, is never drawn, and is
  // kept in the saved copy so it appears by itself once it can be.
  const isVisible = (id: string) => isAllowed(id) && isWidgetBuilt(id)
  const editing = draft !== null
  const shown = (draft ?? active?.widgets ?? []).filter(w => isVisible(w.id))

  const stopEditing = () => {
    setDraft(null)
    setPicking(false)
  }
  const startEditing = () => setDraft((active?.widgets ?? []).filter(w => isVisible(w.id)))
  const save = () => {
    if (active && draft) boards.saveWidgets(active.id, mergeHiddenWidgets(draft, active.widgets, isVisible))
    stopEditing()
  }
  const changeView = (id: string, view: WidgetView) => {
    if (active) boards.saveWidgets(active.id, setWidgetView(active.widgets, id, view))
  }
  const switchTo = (id: string) => {
    stopEditing()
    setConfirmDelete(false)
    setViewingId(id)
  }

  if (boards.isLoading) return null

  return (
    <DashboardPeriodProvider>
      <div className='flex h-full flex-col overflow-hidden'>
        {/* Tabs: one per dashboard, underline style, + New at the end. */}
        <div className='flex shrink-0 items-end gap-1 border-b border-gray-200 bg-white px-6'>
          <nav aria-label='Your dashboards' className='-mb-px flex min-w-0 flex-1 items-end gap-1 overflow-x-auto'>
            {boards.prefs.dashboards.map(d => {
              const current = d.id === active?.id
              return (
                <button
                  key={d.id}
                  type='button'
                  aria-current={current ? 'page' : undefined}
                  onClick={() => switchTo(d.id)}
                  className={cn(
                    'flex shrink-0 items-center gap-1.5 border-b-2 px-3 pb-2.5 pt-3 text-sm transition-colors',
                    current
                      ? 'border-blue-600 font-semibold text-gray-900'
                      : 'border-transparent font-medium text-gray-500 hover:border-gray-300 hover:text-gray-800',
                  )}
                >
                  {d.id === boards.prefs.defaultId && (
                    <Star className='h-3 w-3 fill-amber-400 text-amber-400' aria-label='Default' />
                  )}
                  {d.name}
                </button>
              )
            })}
          </nav>
          <button
            type='button'
            onClick={() => setNaming('new')}
            disabled={!boards.ready || !canAddDashboard(boards.prefs)}
            title={canAddDashboard(boards.prefs) ? 'New dashboard' : 'You have the most dashboards you can keep. Delete one first.'}
            className='mb-1.5 flex shrink-0 items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium text-blue-600 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-40'
          >
            <Plus className='h-4 w-4' /> New dashboard
          </button>
        </div>

        <div className='flex-1 overflow-y-auto bg-gray-50 p-5'>
          {boards.saveFailed && <SaveFailedNote />}
          {!active ? (
            <NoDashboards onCreate={() => setNaming('new')} disabled={!boards.ready} />
          ) : (
            <>
              {editing ? (
                // Edit mode: one clear bar saying what can be done, and how to finish.
                <div className='sticky top-0 z-20 mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50/95 px-4 py-3 shadow-sm backdrop-blur'>
                  <div className='flex min-w-0 items-center gap-3'>
                    <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-600'>
                      <Pencil className='h-4 w-4 text-white' />
                    </span>
                    <div className='min-w-0'>
                      <p className='truncate text-sm font-semibold text-blue-900'>Editing “{active.name}”</p>
                      <p className='text-xs text-blue-700'>Drag a widget by its handle to move it · S, W, F to resize · − to remove</p>
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    <Button size='sm' variant='outline' className='rounded-full bg-white' onClick={() => setPicking(true)}>
                      <Plus className='mr-1 h-3.5 w-3.5' /> Add widgets
                    </Button>
                    <Button size='sm' variant='ghost' className='rounded-full' onClick={stopEditing}>
                      Cancel
                    </Button>
                    <Button size='sm' className='rounded-full px-5' onClick={save} disabled={!boards.ready}>
                      <Check className='mr-1 h-3.5 w-3.5' /> Save
                    </Button>
                  </div>
                </div>
              ) : (
                <div className='mb-4 flex flex-wrap items-center justify-between gap-3'>
                  <div className='min-w-0'>
                    <div className='flex items-center gap-2'>
                      <h1 className='truncate text-lg font-semibold tracking-tight text-gray-900'>{active.name}</h1>
                      {isDefault && (
                        <span className='flex shrink-0 items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-amber-200'>
                          <Star className='h-3 w-3 fill-current' /> Default
                        </span>
                      )}
                    </div>
                    <p className='mt-0.5 text-xs text-gray-500'>
                      {shown.length} widget{shown.length === 1 ? '' : 's'} · live data, refreshed every minute
                    </p>
                  </div>
                  <div className='flex flex-wrap items-center gap-2'>
                    <DashboardPeriodSelect />
                    <Button size='sm' className='rounded-full px-4' onClick={startEditing} disabled={!boards.ready}>
                      <Pencil className='mr-1.5 h-3.5 w-3.5' /> Customize
                    </Button>
                    <DashboardMenu
                      name={active.name}
                      disabled={!boards.ready}
                      canSetDefault={!isDefault}
                      canRestore={changedFromStarter}
                      confirming={confirmDelete}
                      onConfirming={setConfirmDelete}
                      onSetDefault={() => boards.setDefault(active.id)}
                      onRestore={() => boards.restore(active.id)}
                      onRename={() => setNaming('rename')}
                      onDelete={() => {
                        boards.remove(active.id)
                        setConfirmDelete(false)
                        setViewingId(null)
                      }}
                    />
                  </div>
                </div>
              )}

              {shown.length > 0 ? (
                <DashboardGrid
                  widgets={shown}
                  editing={editing}
                  onChange={setDraft}
                  onViewChange={boards.ready ? changeView : undefined}
                />
              ) : (
                <EmptyDashboard
                  onAdd={() => {
                    if (!editing) startEditing()
                    setPicking(true)
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>

      {editing && (
        <AddWidgetsDialog open={picking} onOpenChange={setPicking} draft={draft} allowed={allowed} onChange={setDraft} />
      )}

      {naming === 'new' && (
        <DashboardNameDialog
          open
          onOpenChange={open => !open && setNaming(null)}
          prefs={boards.prefs}
          widgetCount={preset => presetWidgets(preset, isAllowed, isWidgetBuilt).filter(w => isVisible(w.id)).length}
          onSave={(name, preset) => {
            stopEditing()
            setViewingId(boards.create(name, presetWidgets(preset, isAllowed, isWidgetBuilt)))
          }}
        />
      )}
      {naming === 'rename' && active && (
        <DashboardNameDialog
          open
          onOpenChange={open => !open && setNaming(null)}
          prefs={boards.prefs}
          renaming={{ id: active.id, name: active.name }}
          onSave={name => boards.rename(active.id, name)}
        />
      )}
    </DashboardPeriodProvider>
  )
}

function NoDashboards({ onCreate, disabled }: { onCreate: () => void; disabled: boolean }) {
  return (
    <div className='mx-auto flex max-w-md flex-col items-center gap-3 rounded-xl border border-dashed border-gray-300 bg-white px-6 py-16 text-center'>
      <LayoutGrid className='h-8 w-8 text-gray-300' />
      <div>
        <h1 className='text-sm font-semibold text-gray-800'>Build your own dashboards</h1>
        <p className='mt-1 text-xs text-gray-500'>
          Make a dashboard for each thing you track, such as “Inventory” or “QA review”, and pick the widgets it shows.
          Your dashboards are yours: nobody else sees them, and Home stays as it is.
        </p>
      </div>
      <Button size='sm' onClick={onCreate} disabled={disabled}>
        <Plus className='mr-1 h-3.5 w-3.5' /> Create your first dashboard
      </Button>
    </div>
  )
}

function EmptyDashboard({ onAdd }: { onAdd: () => void }) {
  return (
    <div className='flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-gray-300 bg-white py-16 text-center'>
      <LayoutGrid className='h-8 w-8 text-gray-300' />
      <div>
        <p className='text-sm font-medium text-gray-700'>This dashboard has no widgets</p>
        <p className='text-xs text-gray-400'>Add widgets to choose what it shows.</p>
      </div>
      <Button size='sm' onClick={onAdd}>
        <Plus className='mr-1 h-3.5 w-3.5' /> Add widgets
      </Button>
    </div>
  )
}

/**
 * The dashboard's less frequent actions, behind one "⋯" so the header has a
 * single obvious action (Customize). Delete asks again inside the menu.
 */
function DashboardMenu({
  name,
  disabled,
  canSetDefault,
  canRestore,
  confirming,
  onConfirming,
  onSetDefault,
  onRestore,
  onRename,
  onDelete,
}: {
  name: string
  disabled: boolean
  canSetDefault: boolean
  canRestore: boolean
  confirming: boolean
  onConfirming: (confirming: boolean) => void
  onSetDefault: () => void
  onRestore: () => void
  onRename: () => void
  onDelete: () => void
}) {
  const [open, setOpen] = useState(false)
  const close = () => {
    setOpen(false)
    onConfirming(false)
  }
  const item =
    'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40'
  return (
    <Popover open={open} onOpenChange={next => (next ? setOpen(true) : close())}>
      <PopoverTrigger asChild>
        <button
          type='button'
          disabled={disabled}
          aria-label='More actions'
          title='More actions'
          className='flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-800 disabled:opacity-40'
        >
          <MoreHorizontal className='h-4 w-4' />
        </button>
      </PopoverTrigger>
      <PopoverContent align='end' className='w-64 p-1.5'>
        {confirming ? (
          <div className='p-2'>
            <p className='text-sm font-semibold text-gray-900'>Delete “{name}”?</p>
            <p className='mt-1 text-xs text-gray-500'>Its widgets and layout are removed. This cannot be undone.</p>
            <div className='mt-3 flex justify-end gap-2'>
              <Button size='sm' variant='ghost' onClick={() => onConfirming(false)}>
                Keep it
              </Button>
              <Button
                size='sm'
                className='bg-red-600 hover:bg-red-700'
                onClick={() => {
                  onDelete()
                  setOpen(false)
                }}
              >
                <Trash2 className='mr-1 h-3.5 w-3.5' /> Delete
              </Button>
            </div>
          </div>
        ) : (
          <div role='menu'>
            <button type='button' role='menuitem' className={item} disabled={!canSetDefault} onClick={() => { onSetDefault(); close() }}>
              <Star className='h-4 w-4 text-amber-500' />
              <span className='flex-1'>{canSetDefault ? 'Set as default' : 'This is your default'}</span>
            </button>
            <button type='button' role='menuitem' className={item} disabled={!canRestore} onClick={() => { onRestore(); close() }}>
              <RotateCcw className='h-4 w-4 text-gray-400' />
              <span className='flex-1'>Restore original</span>
            </button>
            <button type='button' role='menuitem' className={item} onClick={() => { onRename(); close() }}>
              <Pencil className='h-4 w-4 text-gray-400' />
              <span className='flex-1'>Rename</span>
            </button>
            <div className='my-1 h-px bg-gray-100' />
            <button
              type='button'
              role='menuitem'
              className={cn(item, 'text-red-600 hover:bg-red-50')}
              onClick={() => onConfirming(true)}
            >
              <Trash2 className='h-4 w-4' />
              <span className='flex-1'>Delete dashboard</span>
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
